<template>
    <div>
        <slot
            :filterList="filterList"
            :categories="categories"
            :filteredPosts="filteredPosts"
            :pagedPosts="pagedPosts"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :paginationPages="paginationPages"
            :hasPreviousPage="hasPreviousPage"
            :hasNextPage="hasNextPage"
            :previousLink="previousLink"
            :nextLink="nextLink"
            :switchPage="switchPage"
            :goToPage="goToPage"></slot>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { uniq, groupBy } from 'lodash';
import { getBlogs } from '../services/inventory';

const POSTS_PER_PAGE = 12;
const PAGE_PARAM = 'page';

export default defineComponent({
    name: 'ArticleListHub',
    props: {
        blogId: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const blog = ref(null);

        const categories = ref([]);
        const posts = ref([]);
        const selectedCategory = ref('');
        const filteredPosts = ref([]);
        const currentPage = ref(1);
        const DEFAULT = 'Choose Category';

        const totalPages = computed(() => {
            return Math.ceil(filteredPosts.value.length / POSTS_PER_PAGE) || 1;
        });

        const pagedPosts = computed(() => {
            const startIndex = (currentPage.value - 1) * POSTS_PER_PAGE;

            return filteredPosts.value.slice(startIndex, startIndex + POSTS_PER_PAGE);
        });

        const hasPreviousPage = computed(() => currentPage.value > 1);
        const hasNextPage = computed(() => currentPage.value < totalPages.value);

        const pageURL = function(page) {
            const url = new URL(window.location);
            url.searchParams.set(PAGE_PARAM, page);

            return url.toString();
        }

        const previousLink = computed(() => {
            return hasPreviousPage.value ? pageURL(currentPage.value - 1) : null;
        });

        const nextLink = computed(() => {
            return hasNextPage.value ? pageURL(currentPage.value + 1) : null;
        });

        const pageEntry = function(number) {
            return {
                number: number,
                current: number === currentPage.value,
                link: pageURL(number),
            };
        }

        const paginationPages = computed(() => {
            const pages = [];
            const delta = 2;

            let previous = 0;

            for (let i = 1; i <= totalPages.value; i++) {
                const withinWindow = i === 1
                    || i === totalPages.value
                    || (i >= currentPage.value - delta && i <= currentPage.value + delta);

                if (!withinWindow) {
                    continue;
                }

                if (i - previous === 2) {
                    pages.push(pageEntry(i - 1));
                } else if (i - previous > 2) {
                    pages.push(null);
                }

                pages.push(pageEntry(i));
                previous = i;
            }

            return pages;
        });

        onMounted(async () => {
            const search = new URLSearchParams(window.location.search);
            const requestedPage = parseInt(search.get(PAGE_PARAM), 10);

            currentPage.value = requestedPage > 0 ? requestedPage : 1;

            const blogs = await getBlogs();

            blog.value = blogs.find(item => item.id === props.blogId);

            if (blog.value) {
                posts.value = blog.value.posts.docs;
                filteredPosts.value = blog.value.posts.docs;

                processPosts(posts.value);
            }

            if (currentPage.value > totalPages.value) {
                currentPage.value = totalPages.value;
            }
        })

        const processPosts = function(posts) {
            posts.forEach(post => {
                //Url patch
                post.url = "/" + blog.value.slug + "/posts/" + post.slug;

                //Process categories
                if (post.categories) {
                    post.categories.forEach(category => {
                        if (category.title) {
                            categories.value.push(category.title)
                        }
                    })
                }
            })

            categories.value = uniq(categories.value);
            categories.value = [DEFAULT, ...categories.value]
        }

        const filterList = function(event) {
            selectedCategory.value = event.target.value;

            filteredPosts.value = posts.value.filter(post => {
                if (selectedCategory.value == DEFAULT) {
                    return true;
                }

                if (post.categories) {
                    const groupedCategories = groupBy(post.categories, category => category.title);

                    return Object.keys(groupedCategories).includes(selectedCategory.value);
                } else {
                    return false;
                }
            })

            goToPage(1);
        }

        const scrollToGrid = function() {
            const grid = document.querySelector('.article-list__grid');

            if (!grid) {
                return;
            }

            const globalGroup = document.querySelector('.global-group');
            const headerHeight = globalGroup && window.getComputedStyle(globalGroup).position === 'sticky'
                ? globalGroup.offsetHeight
                : 0;

            window.scrollTo({
                top: grid.getBoundingClientRect().top + window.pageYOffset - headerHeight,
                behavior: 'smooth',
            });
        }

        const goToPage = function(page) {
            currentPage.value = Math.min(Math.max(parseInt(page, 10), 1), totalPages.value);

            window.history.pushState({}, '', pageURL(currentPage.value));

            scrollToGrid();
        }

        const switchPage = function(step) {
            goToPage(currentPage.value + parseInt(step, 10));
        }

        return {
            filteredPosts: filteredPosts,
            filterList: filterList,
            categories: categories,
            pagedPosts: pagedPosts,
            currentPage: currentPage,
            totalPages: totalPages,
            paginationPages: paginationPages,
            hasPreviousPage: hasPreviousPage,
            hasNextPage: hasNextPage,
            previousLink: previousLink,
            nextLink: nextLink,
            switchPage: switchPage,
            goToPage: goToPage,
        }
    }
});

</script>


